import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import DarkModeToggle from './DarkModeToggle';

// Approved map (#19): Events · Garage · Privacy · Pro · Explore.
const NAV_LINKS = [
  { to: '/events', label: 'Events' },
  { to: '/garage', label: 'Garage' },
  { to: '/privacy', label: 'Privacy' },
  { to: '/pro', label: 'Pro' },
];

const EXPLORE_LINKS = [
  { to: '/social', label: 'Community' },
  { to: '/hosting', label: 'Hosting' },
  { to: '/grouppulse', label: 'GroupPulse' },
  { to: '/help', label: 'Guides & Help' },
  { to: '/about', label: 'About' },
];

const HIDDEN_PREFIXES = ['/admin', '/auth', '/email-verified'];

const MarketingHeader: React.FC = () => {
  const location = useLocation();
  const { colors, dsColors } = useTheme();
  const [exploreOpen, setExploreOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const exploreRef = useRef<HTMLDivElement>(null);
  const exploreButtonRef = useRef<HTMLButtonElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setExploreOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  const ringStyle = { '--tw-ring-color': dsColors.primary } as React.CSSProperties;
  const focusClasses = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-0';

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const exploreActive = EXPLORE_LINKS.some((l) => isActive(l.to));

  const closeExplore = (returnFocus = false) => {
    setExploreOpen(false);
    if (returnFocus) exploreButtonRef.current?.focus();
  };

  const onExploreBlur = (e: React.FocusEvent) => {
    if (!exploreRef.current?.contains(e.relatedTarget as Node)) closeExplore();
  };

  if (HIDDEN_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'))) {
    return null;
  }

  const linkStyle = (active: boolean): React.CSSProperties => ({
    color: active ? dsColors.primary : colors.textSecondary,
  });

  const navLinkClass = `px-3 py-2 rounded-lg text-sm font-semibold transition-colors rp-nav-link ${focusClasses}`;

  return (
    <header
        className="sticky top-0 z-50 relative -mb-[68px] sm:-mb-[72px]"
      >
        <div className="mx-auto px-3 sm:px-4 max-w-7xl pt-3 sm:pt-4">
          {/* Two islands: transparent row, treatments live on each group only.
              flex-wrap is the extreme-zoom fallback — at very large text scaling the
              rem-sized islands can exceed the viewport, so they stack rather than
              force horizontal scroll. */}
          <div className="flex flex-wrap items-start justify-between gap-y-2">
            <Link
              to="/"
              className={`inline-flex items-center gap-2.5 h-14 rounded-2xl border shadow-lg px-2.5 sm:px-4 backdrop-blur-md ${focusClasses}`}
              style={{
                backgroundColor: `${colors.surface}d9`,
                borderColor: colors.border,
                ...ringStyle,
              }}
              aria-label="RidePulse home"
            >
              <img
                src="/assets/images/logo-256.png"
                alt=""
                className="h-9 w-9 rounded-lg"
                style={{ border: `1px solid ${colors.border}` }}
              />
              <span className="hidden min-[420px]:inline text-lg font-bold tracking-tight" style={{ color: colors.textPrimary }}>
                RidePulse
              </span>
            </Link>

            <div
              className="flex items-center h-14 rounded-2xl border shadow-lg px-1.5 sm:px-2 backdrop-blur-md"
              style={{
                backgroundColor: `${colors.surface}d9`,
                borderColor: colors.border,
              }}
            >
            <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className={navLinkClass}
                  style={{ ...ringStyle, ...linkStyle(isActive(l.to)) }}
                  aria-current={isActive(l.to) ? 'page' : undefined}
                >
                  {l.label}
                </Link>
              ))}

              <div
                ref={exploreRef}
                className="relative"
                onMouseEnter={() => setExploreOpen(true)}
                onMouseLeave={() => setExploreOpen(false)}
                onBlur={onExploreBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') closeExplore(true);
                }}
              >
                <button
                  ref={exploreButtonRef}
                  type="button"
                  className={`${navLinkClass} inline-flex items-center gap-1.5`}
                  style={{ ...ringStyle, ...linkStyle(exploreActive) }}
                  aria-expanded={exploreOpen}
                  aria-controls="explore-dropdown"
                  aria-current={exploreActive ? 'page' : undefined}
                  onClick={() => setExploreOpen((v) => !v)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown' && !exploreOpen) setExploreOpen(true);
                  }}
                >
                  Explore
                  <svg
                    width="14" height="14" viewBox="0 0 24 24" fill="none"
                    className={`motion-safe:transition-transform ${exploreOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  >
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {exploreOpen && (
                  <ul
                    id="explore-dropdown"
                    className="absolute right-0 top-full mt-1 min-w-[10rem] rounded-xl border py-1.5 shadow-xl motion-safe:animate-fade-in before:absolute before:inset-x-0 before:-top-1.5 before:h-1.5 before:content-['']"
                    style={{
                      backgroundColor: colors.surface,
                      borderColor: colors.border,
                    }}
                  >
                    {EXPLORE_LINKS.map((l) => (
                      <li key={l.to}>
                        <Link
                          to={l.to}
                          className={`block px-4 py-2.5 text-sm font-semibold transition-colors rp-nav-link ${focusClasses}`}
                          style={{ ...ringStyle, ...linkStyle(isActive(l.to)) }}
                          aria-current={isActive(l.to) ? 'page' : undefined}
                        >
                          {l.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </nav>

            <div
              className="hidden md:block w-px h-6 mx-1.5"
              style={{ backgroundColor: colors.border }}
              aria-hidden="true"
            />
            <div className="flex items-center gap-2 sm:gap-3">
              <DarkModeToggle inline />
              <button
                ref={menuButtonRef}
                type="button"
                className={`md:hidden inline-flex items-center justify-center w-11 h-11 rounded-lg ${focusClasses}`}
                style={{ ...ringStyle, color: colors.textPrimary }}
                aria-expanded={mobileOpen}
                aria-controls="mobile-menu"
                aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
                onClick={() => setMobileOpen((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setMobileOpen(false);
                    menuButtonRef.current?.focus();
                  }
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  {mobileOpen ? (
                    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  ) : (
                    <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  )}
                </svg>
              </button>
            </div>
            </div>
          </div>
        </div>

        {mobileOpen && (
          <nav
            id="mobile-menu"
            className="md:hidden absolute left-3 right-3 sm:left-4 sm:right-4 rounded-2xl border shadow-xl motion-safe:animate-fade-in backdrop-blur-md"
            style={{ top: 'calc(100% + 6px)', borderColor: colors.border, backgroundColor: `${colors.surface}f2` }}
            aria-label="Mobile"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setMobileOpen(false);
                menuButtonRef.current?.focus();
              }
            }}
          >
            <ul className="container mx-auto px-4 sm:px-6 max-w-7xl py-3 space-y-1">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className={`block px-3 py-3 rounded-lg text-base font-semibold transition-colors rp-nav-link ${focusClasses}`}
                    style={{ ...ringStyle, ...linkStyle(isActive(l.to)) }}
                    aria-current={isActive(l.to) ? 'page' : undefined}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <p
                  className="px-3 pt-2 pb-1 text-xs font-bold uppercase tracking-widest"
                  style={{ color: colors.textSecondary }}
                >
                  Explore
                </p>
                <ul className="space-y-1">
                  {EXPLORE_LINKS.map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        className={`block px-3 py-3 rounded-lg text-base font-semibold transition-colors rp-nav-link ${focusClasses}`}
                        style={{ ...ringStyle, ...linkStyle(isActive(l.to)) }}
                        aria-current={isActive(l.to) ? 'page' : undefined}
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            </ul>
          </nav>
        )}
    </header>
  );
};

export default MarketingHeader;
